import React, { useMemo, useState } from 'react';
import { Card, Typography, Form, Input, Button, Select, Divider, Row, Col, App, Flex } from 'antd';
import { useLanguage } from 'src/providers/LanguageProvider';
import { useDispatch, useSelector } from 'react-redux';
import { MailOutlined, PhoneOutlined } from '@ant-design/icons';
import api from 'src/services/api';
import { setAccount } from 'src/store/authSlice';

const { Title } = Typography;

const ProfileUpdate: React.FC = () => {
  const { t } = useLanguage();
  const { message: messageApi } = App.useApp();

  const [saving, setSaving] = useState(false);
  const [profileForm] = Form.useForm();

  const dispatch = useDispatch();
  const profileOnStore = useSelector((e: any) => e.auth.account);

  const countryOptions = useMemo(
    () => [
      { value: 'TR', label: t('COUNTRY_TR') },
      { value: 'NL', label: t('COUNTRY_NL') },
      { value: 'DE', label: t('COUNTRY_DE') },
      { value: 'US', label: t('COUNTRY_US') },
      { value: 'FR', label: t('COUNTRY_FR') },
      { value: 'GB', label: t('COUNTRY_GB') },
      { value: 'BE', label: t('COUNTRY_BE') },
    ],
    [t]
  );

  const languageOptions = useMemo(
    () => [
      { value: 'TR', label: t('LANG_NAME_TR') },
      { value: 'EN', label: t('LANG_NAME_EN') },
      { value: 'NL', label: t('LANG_NAME_NL') },
      { value: 'DE', label: t('LANG_NAME_DE') },
      { value: 'FR', label: t('LANG_NAME_FR') },
    ],
    [t]
  );

  const updateAccount = async (updatedData: any) => {
    return api.put(`/accounts/${profileOnStore.Id}`, updatedData);
  };

  const handleUpdateProfile = async (values: any) => {
    setSaving(true);
    try {
      const payload = {
        FirstName: values.FirstName?.trim(),
        LastName: values.LastName?.trim(),
        Phone: values.Phone?.trim(),
        Country: values.Country || null,
        Language: values.Language || null,
      };
      await updateAccount(payload);
      messageApi.success(t('PROFILE_UPDATE_SUCCESS'));
      dispatch(setAccount({ ...profileOnStore, ...payload }));
    } catch {
      messageApi.error(t('PROFILE_UPDATE_ERROR'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="card" style={{ borderRadius: 14, height: '100%' }}>
      <Title level={5} style={{ marginTop: 0 }}>
        {t('PROFILE_UPDATE')}
      </Title>
      <Divider style={{ margin: '8px 0 16px' }} />

      <Form
        id="profile-form"
        form={profileForm}
        layout="vertical"
        initialValues={{
          FirstName: profileOnStore?.FirstName,
          LastName: profileOnStore?.LastName,
          Email: profileOnStore?.Email,
          Phone: profileOnStore?.Phone,
          Country: profileOnStore?.Country,
          Language: profileOnStore?.Language,
        }}
        onFinish={handleUpdateProfile}
      >
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={t('LABEL_FIRSTNAME')}
              name="FirstName"
              rules={[{ required: true, message: t('ERROR_FIRSTNAME_REQUIRED') }]}
            >
              <Input placeholder={t('PLACEHOLDER_FIRSTNAME')} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={t('LABEL_LASTNAME')}
              name="LastName"
              rules={[{ required: true, message: t('ERROR_LASTNAME_REQUIRED') }]}
            >
              <Input placeholder={t('PLACEHOLDER_LASTNAME')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={t('LABEL_EMAIL')}
          name="Email"
          rules={[
            { required: true, message: t('ERROR_EMAIL_REQUIRED') },
            { type: 'email', message: t('ERROR_EMAIL_INVALID') },
          ]}
        >
          <Input disabled prefix={<MailOutlined />} placeholder={t('PLACEHOLDER_EMAIL')} />
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item label={t('LABEL_PHONE')} name="Phone">
              <Input prefix={<PhoneOutlined />} placeholder={t('PLACEHOLDER_PHONE')} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t('LABEL_COUNTRY')} name="Country">
              <Select
                showSearch
                allowClear
                placeholder={t('PLACEHOLDER_COUNTRY')}
                options={countryOptions}
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={t('LABEL_LANGUAGE')} name="Language">
          <Select allowClear placeholder={t('PLACEHOLDER_LANGUAGE')} options={languageOptions} />
        </Form.Item>

        <Flex justify="end" gap={8} wrap="wrap">
          <Button htmlType="reset">{t('BTN_RESET')}</Button>
          <Button type="primary" htmlType="submit" loading={saving}>
            {t('BTN_SAVE')}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};

export default ProfileUpdate;
